import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import {
  categoryTranslationFragment,
  collectionTranslationFragment,
  comboTranslationFragment,
  cornerMarkTranslationFragment,
  extrachargeTranslationFragment,
  hotSearchTranslationFragment,
  loginPopupTranslationFragment,
  menuBannerTranslationFragment,
  menuItemTranslationFragment,
  pageHeaderTranslationFragment,
  pageTranslationFragment,
  productActivationSettingTranslationFragment,
  productLensTypeTranslationFragment,
  productTagsTranslationFragment,
  productTranslationFragment,
  productTypeTranslationFragment,
  productVariantTranslationFragment,
  saleTranslationFragment,
  shippingMethodTranslationFragment,
  shopTranslationFragment,
  voucherTranslationFragment
} from "@saleor/fragments/translations";
import gql from "graphql-tag";

import { TypedQuery } from "../queries";
import {
  CategoryTranslationDetails,
  CategoryTranslationDetailsVariables
} from "./types/CategoryTranslationDetails";
import {
  CategoryTranslations,
  CategoryTranslationsVariables
} from "./types/CategoryTranslations";
import {
  CollectionTranslationDetails,
  CollectionTranslationDetailsVariables
} from "./types/CollectionTranslationDetails";
import {
  CollectionTranslations,
  CollectionTranslationsVariables
} from "./types/CollectionTranslations";
import {
  ComboTranslationDetails,
  ComboTranslationDetailsVariables
} from "./types/ComboTranslationDetails";
import {
  ComboTranslations,
  ComboTranslationsVariables
} from "./types/ComboTranslations";
import {
  CornerMarkTranslationDetails,
  CornerMarkTranslationDetailsVariables
} from "./types/CornerMarkTranslationDetails";
import {
  CornerMarkTranslations,
  CornerMarkTranslationsVariables
} from "./types/CornerMarkTranslations";
import {
  ExtraChargeTranslationDetails,
  ExtraChargeTranslationDetailsVariables
} from "./types/ExtraChargeTranslationDetails";
import {
  ExtraChargeTranslations,
  ExtraChargeTranslationsVariables
} from "./types/ExtraChargeTranslations";
import {
  HotWordTranslationDetails,
  HotWordTranslationDetailsVariables
} from "./types/HotWordTranslationDetails";
import {
  HotWordTranslations,
  HotWordTranslationsVariables
} from "./types/HotWordTranslations";
import {
  LoginPopupTranslationDetails,
  LoginPopupTranslationDetailsVariables
} from "./types/LoginPopupTranslationDetails";
import {
  LoginPopupTranslations,
  LoginPopupTranslationsVariables
} from "./types/LoginPopupTranslations";
import {
  MenuBannerTranslationDetails,
  MenuBannerTranslationDetailsVariables
} from "./types/MenuBannerTranslationDetails";
import {
  MenuBannerTranslations,
  MenuBannerTranslationsVariables
} from "./types/MenuBannerTranslations";
import {
  MenuItemTranslationDetails,
  MenuItemTranslationDetailsVariables
} from "./types/MenuItemTranslationDetails";
import {
  MenuItemTranslations,
  MenuItemTranslationsVariables
} from "./types/MenuItemTranslations";
import {
  PageHeaderTranslationDetails,
  PageHeaderTranslationDetailsVariables
} from "./types/PageHeaderTranslationDetails";
import {
  PageHeaderTranslations,
  PageHeaderTranslationsVariables
} from "./types/PageHeaderTranslations";
import {
  PageTranslationDetails,
  PageTranslationDetailsVariables
} from "./types/PageTranslationDetails";
import {
  PageTranslations,
  PageTranslationsVariables
} from "./types/PageTranslations";
import {
  ProductActivationSettingDetails,
  ProductActivationSettingDetailsVariables
} from "./types/ProductActivationSettingDetails";
import {
  ProductActivationSettingTranslations,
  ProductActivationSettingTranslationsVariables
} from "./types/ProductActivationSettingTranslations";
import {
  ProductLensTypeTranslationDetails,
  ProductLensTypeTranslationDetailsVariables
} from "./types/ProductLensTypeTranslationDetails";
import {
  ProductLensTypeTranslations,
  ProductLensTypeTranslationsVariables
} from "./types/ProductLensTypeTranslations";
import {
  ProductTagsTranslations,
  ProductTagsTranslationsVariables
} from "./types/ProductTagsTranslations";
import {
  ProductTagTranslationDetails,
  ProductTagTranslationDetailsVariables
} from "./types/ProductTagTranslationDetails";
import {
  ProductTranslationDetails,
  ProductTranslationDetailsVariables
} from "./types/ProductTranslationDetails";
import {
  ProductTranslations,
  ProductTranslationsVariables
} from "./types/ProductTranslations";
import {
  ProductTypeTranslationDetails,
  ProductTypeTranslationDetailsVariables
} from "./types/ProductTypeTranslationDetails";
import {
  ProductTypeTranslations,
  ProductTypeTranslationsVariables
} from "./types/ProductTypeTranslations";
import {
  ProductVariantTranslationDetails,
  ProductVariantTranslationDetailsVariables
} from "./types/ProductVariantTranslationDetails";
import {
  ProductVariantTranslations,
  ProductVariantTranslationsVariables
} from "./types/ProductVariantTranslations";
import {
  SaleTranslationDetails,
  SaleTranslationDetailsVariables
} from "./types/SaleTranslationDetails";
import {
  SaleTranslations,
  SaleTranslationsVariables
} from "./types/SaleTranslations";
import {
  ShippingMethodTranslationDetails,
  ShippingMethodTranslationDetailsVariables
} from "./types/ShippingMethodTranslationDetails";
import {
  ShippingMethodTranslations,
  ShippingMethodTranslationsVariables
} from "./types/ShippingMethodTranslations";
import {
  ShopTranslationDetails,
  ShopTranslationDetailsVariables
} from "./types/ShopTranslationDetails";
import {
  ShopTranslations,
  ShopTranslationsVariables
} from "./types/ShopTranslations";
import {
  VoucherTranslationDetails,
  VoucherTranslationDetailsVariables
} from "./types/VoucherTranslationDetails";
import {
  VoucherTranslations,
  VoucherTranslationsVariables
} from "./types/VoucherTranslations";

const categoryTranslations = gql`
  ${pageInfoFragment}
  ${categoryTranslationFragment}
  query CategoryTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: CategoryFilterInput
  ) {
    categories(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...CategoryTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedCategoryTranslations = TypedQuery<
  CategoryTranslations,
  CategoryTranslationsVariables
>(categoryTranslations);

const collectionTranslations = gql`
  ${pageInfoFragment}
  ${collectionTranslationFragment}
  query CollectionTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: CollectionFilterInput
  ) {
    collections(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...CollectionTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedCollectionTranslations = TypedQuery<
  CollectionTranslations,
  CollectionTranslationsVariables
>(collectionTranslations);

const productTranslations = gql`
  ${pageInfoFragment}
  ${productTranslationFragment}
  query ProductTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: ProductFilterInput
  ) {
    products(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...ProductTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedProductTranslations = TypedQuery<
  ProductTranslations,
  ProductTranslationsVariables
>(productTranslations);

const productVariantTranslations = gql`
  ${pageInfoFragment}
  ${productVariantTranslationFragment}
  query ProductVariantTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: ProductVariantFilterInput
  ) {
    productVariants(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...ProductVariantTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedProductVariantTranslations = TypedQuery<
  ProductVariantTranslations,
  ProductVariantTranslationsVariables
>(productVariantTranslations);

const pageTranslations = gql`
  ${pageInfoFragment}
  ${pageTranslationFragment}
  query PageTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: PageFilterInput
  ) {
    pages(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...PageTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedPageTranslations = TypedQuery<
  PageTranslations,
  PageTranslationsVariables
>(pageTranslations);
const pageHeaderTranslations = gql`
  ${pageInfoFragment}
  ${pageHeaderTranslationFragment}
  query PageHeaderTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: PageHeaderFilterInput
  ) {
    pageHeaders(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...PageHeaderTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedPageHeaderTranslations = TypedQuery<
  PageHeaderTranslations,
  PageHeaderTranslationsVariables
>(pageHeaderTranslations);

const productTagsTranslations = gql`
  ${pageInfoFragment}
  ${productTagsTranslationFragment}
  query ProductTagsTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: ProductTagsFilterInput
  ) {
    producttags(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...ProductTagsTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedProductTagsTranslations = TypedQuery<
  ProductTagsTranslations,
  ProductTagsTranslationsVariables
>(productTagsTranslations);

const menuItemTranslations = gql`
  ${pageInfoFragment}
  ${menuItemTranslationFragment}
  query MenuItemTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: MenuItemFilterInput
  ) {
    menuItems(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      sortBy: { field: ORDER, direction: ASC }
    ) {
      edges {
        node {
          ...MenuItemTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedMenuItemTranslations = TypedQuery<
  MenuItemTranslations,
  MenuItemTranslationsVariables
>(menuItemTranslations);

const menuBannerTranslations = gql`
  ${pageInfoFragment}
  ${menuBannerTranslationFragment}
  query MenuBannerTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    menuBanners(before: $before, after: $after, first: $first, last: $last) {
      edges {
        node {
          ...MenuBannerTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedMenuBannerTranslations = TypedQuery<
  MenuBannerTranslations,
  MenuBannerTranslationsVariables
>(menuBannerTranslations);

const hotWordTranslations = gql`
  ${pageInfoFragment}
  ${hotSearchTranslationFragment}
  query HotWordTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    hotSearch(before: $before, after: $after, first: $first, last: $last) {
      edges {
        node {
          ...HotSearchTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedHotWordTranslations = TypedQuery<
  HotWordTranslations,
  HotWordTranslationsVariables
>(hotWordTranslations);

const voucherTranslations = gql`
  ${pageInfoFragment}
  ${voucherTranslationFragment}
  query VoucherTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: VoucherFilterInput
    $languageCode: String
  ) {
    vouchersByLanguage(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      languageCode: $languageCode
    ) {
      edges {
        node {
          ...VoucherTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedVoucherTranslations = TypedQuery<
  VoucherTranslations,
  VoucherTranslationsVariables
>(voucherTranslations);

const saleTranslations = gql`
  ${pageInfoFragment}
  ${saleTranslationFragment}
  query SaleTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: SaleFilterInput
    $languageCode: String
  ) {
    salesByLanguage(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
      languageCode: $languageCode
    ) {
      edges {
        node {
          ...SaleTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedSaleTranslations = TypedQuery<
  SaleTranslations,
  SaleTranslationsVariables
>(saleTranslations);

const productTypeTranslations = gql`
  ${pageInfoFragment}
  query ProductTypeTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: ProductTypeFilterInput
  ) {
    productTypes(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          id
          name
          productAttributes {
            id
            showName
            name
            translation(languageCode: $language) {
              id
              name
              showName
            }
          }
          # variantAttributes {
          #   id
          #   showName
          #   name
          #   translation(languageCode: $language) {
          #     id
          #     name
          #     showName
          #   }
          # }
          # skuAttributes {
          #   id
          #   showName
          #   name
          #   translation(languageCode: $language) {
          #     id
          #     name
          #     showName
          #   }
          # }
          translation(languageCode: $language) {
            id
            name
          }
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedProductTypeTranslations = TypedQuery<
  ProductTypeTranslations,
  ProductTypeTranslationsVariables
>(productTypeTranslations);

const productTranslationDetails = gql`
  ${productTranslationFragment}
  query ProductTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    product(id: $id) {
      ...ProductTranslationFragment
    }
  }
`;
export const TypedProductTranslationDetails = TypedQuery<
  ProductTranslationDetails,
  ProductTranslationDetailsVariables
>(productTranslationDetails);

const productVariantTranslationDetails = gql`
  ${productVariantTranslationFragment}
  query ProductVariantTranslationDetails(
    $id: ID!
    $language: LanguageCodeEnum!
  ) {
    productVariant(id: $id) {
      ...ProductVariantTranslationFragment
    }
  }
`;
export const TypedProductVariantTranslationDetails = TypedQuery<
  ProductVariantTranslationDetails,
  ProductVariantTranslationDetailsVariables
>(productVariantTranslationDetails);

const categoryTranslationDetails = gql`
  ${categoryTranslationFragment}
  query CategoryTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    category(id: $id) {
      ...CategoryTranslationFragment
    }
  }
`;
export const TypedCategoryTranslationDetails = TypedQuery<
  CategoryTranslationDetails,
  CategoryTranslationDetailsVariables
>(categoryTranslationDetails);

const collectionTranslationDetails = gql`
  ${collectionTranslationFragment}
  query CollectionTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    collection(id: $id) {
      ...CollectionTranslationFragment
    }
  }
`;
export const TypedCollectionTranslationDetails = TypedQuery<
  CollectionTranslationDetails,
  CollectionTranslationDetailsVariables
>(collectionTranslationDetails);

const pageTranslationDetails = gql`
  ${pageTranslationFragment}
  query PageTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    page(id: $id) {
      ...PageTranslationFragment
    }
  }
`;
export const TypedPageTranslationDetails = TypedQuery<
  PageTranslationDetails,
  PageTranslationDetailsVariables
>(pageTranslationDetails);

const pageHeaderTranslationDetails = gql`
  ${pageHeaderTranslationFragment}
  query PageHeaderTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    pageHeader(id: $id) {
      ...PageHeaderTranslationFragment
    }
  }
`;

export const TypedPageHeaderTranslationDetails = TypedQuery<
  PageHeaderTranslationDetails,
  PageHeaderTranslationDetailsVariables
>(pageHeaderTranslationDetails);

const saleTranslationDetails = gql`
  ${saleTranslationFragment}
  query SaleTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    sale(id: $id) {
      ...SaleTranslationFragment
    }
  }
`;
export const TypedSaleTranslationDetails = TypedQuery<
  SaleTranslationDetails,
  SaleTranslationDetailsVariables
>(saleTranslationDetails);

const voucherTranslationDetails = gql`
  ${voucherTranslationFragment}
  query VoucherTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    voucher(id: $id) {
      ...VoucherTranslationFragment
    }
  }
`;
export const TypedVoucherTranslationDetails = TypedQuery<
  VoucherTranslationDetails,
  VoucherTranslationDetailsVariables
>(voucherTranslationDetails);

const productTypeTranslationDetails = gql`
  ${productTypeTranslationFragment}
  query ProductTypeTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    productType(id: $id) {
      ...ProductTypeTranslationFragment
    }
  }
`;
export const TypedProductTypeTranslationDetails = TypedQuery<
  ProductTypeTranslationDetails,
  ProductTypeTranslationDetailsVariables
>(productTypeTranslationDetails);

const shopTranslations = gql`
  ${pageInfoFragment}
  ${shopTranslationFragment}
  query ShopTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    shopSettings(before: $before, after: $after, first: $first, last: $last) {
      edges {
        node {
          ...ShopTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedShopTranslations = TypedQuery<
  ShopTranslations,
  ShopTranslationsVariables
>(shopTranslations);

const cornerMarkTranslations = gql`
  ${pageInfoFragment}
  ${cornerMarkTranslationFragment}
  query CornerMarkTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: CornerMarkFilterInput
  ) {
    cornerMarks(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...CornerMarkTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedCornerMarkTranslations = TypedQuery<
  CornerMarkTranslations,
  CornerMarkTranslationsVariables
>(cornerMarkTranslations);

const shopTranslationDetails = gql`
  ${shopTranslationFragment}
  query ShopTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    shopSetting(id: $id) {
      ...ShopTranslationFragment
    }
  }
`;

export const TypedShopTranslationDetails = TypedQuery<
  ShopTranslationDetails,
  ShopTranslationDetailsVariables
>(shopTranslationDetails);

const extrachargeTranslations = gql`
  ${pageInfoFragment}
  ${extrachargeTranslationFragment}
  query ExtraChargeTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    extracharges(before: $before, after: $after, first: $first, last: $last) {
      edges {
        node {
          ...ExtraChargeTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedExtraChargeTranslations = TypedQuery<
  ExtraChargeTranslations,
  ExtraChargeTranslationsVariables
>(extrachargeTranslations);

const extrachargeTranslationDetails = gql`
  ${extrachargeTranslationFragment}
  query ExtraChargeTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    extracharge(id: $id) {
      ...ExtraChargeTranslationFragment
    }
  }
`;

export const TypedExtraChargeTranslationDetails = TypedQuery<
  ExtraChargeTranslationDetails,
  ExtraChargeTranslationDetailsVariables
>(extrachargeTranslationDetails);

const productTagTranslationDetails = gql`
  ${productTagsTranslationFragment}
  query ProductTagTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    producttag(id: $id) {
      ...ProductTagsTranslationFragment
    }
  }
`;

export const TypedProductTagTranslationDetails = TypedQuery<
  ProductTagTranslationDetails,
  ProductTagTranslationDetailsVariables
>(productTagTranslationDetails);

const menuItemTranslationDetails = gql`
  ${menuItemTranslationFragment}
  query MenuItemTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    menuItem(id: $id) {
      ...MenuItemTranslationFragment
    }
  }
`;

export const TypedMenuItemTranslationDetails = TypedQuery<
  MenuItemTranslationDetails,
  MenuItemTranslationDetailsVariables
>(menuItemTranslationDetails);

const menuBannerTranslationDetails = gql`
  ${menuBannerTranslationFragment}
  query MenuBannerTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    menuBanner(id: $id) {
      ...MenuBannerTranslationFragment
    }
  }
`;
export const TypedMenuBannerTranslationDetails = TypedQuery<
  MenuBannerTranslationDetails,
  MenuBannerTranslationDetailsVariables
>(menuBannerTranslationDetails);
const hotWordTranslationDetails = gql`
  ${hotSearchTranslationFragment}
  query HotWordTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    hotWord(id: $id) {
      ...HotSearchTranslationFragment
    }
  }
`;
export const TypedHotWordTranslationDetails = TypedQuery<
  HotWordTranslationDetails,
  HotWordTranslationDetailsVariables
>(hotWordTranslationDetails);

const cornerMarkTranslationDetails = gql`
  ${cornerMarkTranslationFragment}
  query CornerMarkTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    cornerMark(id: $id) {
      ...CornerMarkTranslationFragment
    }
  }
`;
export const TypedCornerMarkTranslationDetails = TypedQuery<
  CornerMarkTranslationDetails,
  CornerMarkTranslationDetailsVariables
>(cornerMarkTranslationDetails);

const shippingMethodTranslationDetails = gql`
  ${shippingMethodTranslationFragment}
  query ShippingMethodTranslationDetails(
    $id: ID!
    $language: LanguageCodeEnum!
  ) {
    shippingMethod(id: $id) {
      ...ShippingMethodTranslationFragment
    }
  }
`;
export const TypedShippingMethodTranslationDetails = TypedQuery<
  ShippingMethodTranslationDetails,
  ShippingMethodTranslationDetailsVariables
>(shippingMethodTranslationDetails);

const shippingMethodTranslations = gql`
  ${pageInfoFragment}
  ${shippingMethodTranslationFragment}
  query ShippingMethodTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    shippingMethods(
      before: $before
      after: $after
      first: $first
      last: $last
    ) {
      edges {
        node {
          ...ShippingMethodTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedShippingMethodTranslations = TypedQuery<
  ShippingMethodTranslations,
  ShippingMethodTranslationsVariables
>(shippingMethodTranslations);

const comboTranslations = gql`
  ${pageInfoFragment}
  ${comboTranslationFragment}
  query ComboTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: ComboFilterInput
  ) {
    combos(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          ...ComboTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedComboTranslations = TypedQuery<
  ComboTranslations,
  ComboTranslationsVariables
>(comboTranslations);

const comboTranslationDetails = gql`
  ${comboTranslationFragment}
  query ComboTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    comboById(id: $id) {
      ...ComboTranslationFragment
    }
  }
`;
export const TypedComboTranslationDetails = TypedQuery<
  ComboTranslationDetails,
  ComboTranslationDetailsVariables
>(comboTranslationDetails);

const loginPopupTranslations = gql`
  ${pageInfoFragment}
  ${loginPopupTranslationFragment}
  query LoginPopupTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    loginpopup(before: $before, after: $after, first: $first, last: $last) {
      edges {
        node {
          ...LoginPopupTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedLoginPopupTranslations = TypedQuery<
  LoginPopupTranslations,
  LoginPopupTranslationsVariables
>(loginPopupTranslations);

const loginPopupTranslationDetails = gql`
  ${loginPopupTranslationFragment}
  query LoginPopupTranslationDetails($id: ID!, $language: LanguageCodeEnum!) {
    loginpopupById(id: $id) {
      ...LoginPopupTranslationFragment
    }
  }
`;
export const TypedLoginPopupTranslationDetails = TypedQuery<
  LoginPopupTranslationDetails,
  LoginPopupTranslationDetailsVariables
>(loginPopupTranslationDetails);

const productActivationSettingTranslations = gql`
  ${pageInfoFragment}
  ${productActivationSettingTranslationFragment}
  query ProductActivationSettingTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    productActivationSettings(
      before: $before
      after: $after
      first: $first
      last: $last
    ) {
      edges {
        node {
          ...ProductActivationSettingTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedProductActivationSettingTranslations = TypedQuery<
  ProductActivationSettingTranslations,
  ProductActivationSettingTranslationsVariables
>(productActivationSettingTranslations);

const productActivationSettingDetails = gql`
  ${productActivationSettingTranslationFragment}
  query ProductActivationSettingDetails(
    $id: ID!
    $language: LanguageCodeEnum!
  ) {
    productActivationSettingById(id: $id) {
      ...ProductActivationSettingTranslationFragment
    }
  }
`;
export const TypedProductActivationSettingDetails = TypedQuery<
  ProductActivationSettingDetails,
  ProductActivationSettingDetailsVariables
>(productActivationSettingDetails);

const productLensTypeTranslations = gql`
  ${pageInfoFragment}
  ${productLensTypeTranslationFragment}
  query ProductLensTypeTranslations(
    $language: LanguageCodeEnum!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    productLensTypes(
      before: $before
      after: $after
      first: $first
      last: $last
    ) {
      edges {
        node {
          ...ProductLensTypeTranslationFragment
        }
      }
      pageInfo {
        ...PageInfoFragment
      }
    }
  }
`;
export const TypedProductLensTypeTranslations = TypedQuery<
  ProductLensTypeTranslations,
  ProductLensTypeTranslationsVariables
>(productLensTypeTranslations);

const productLensTypeTranslationDetails = gql`
  ${productLensTypeTranslationFragment}
  query ProductLensTypeTranslationDetails(
    $id: ID!
    $language: LanguageCodeEnum!
  ) {
    productLensType(id: $id) {
      ...ProductLensTypeTranslationFragment
    }
  }
`;
export const TypedProductLensTypeTranslationDetails = TypedQuery<
  ProductLensTypeTranslationDetails,
  ProductLensTypeTranslationDetailsVariables
>(productLensTypeTranslationDetails);
